import { PublicAccessCarInteriorSectionDto } from '../types/models.types';
import { getRatingMessage } from '@/helpers/ratingState';
import { AssessmentItem } from '@/components/CarDetailsAssessment';
import { InspectionItem } from '@/components/InspectionList/InspectionList.types';
import {
  mapScoreToNumber,
  mapAssessmentStatus,
  mapVisualAssessmentToRating,
  mapEquipmentStatusToBadge,
  mapAssessmentStatusInner,
  mapAssessmentStatusAsh,
} from './mapper-helpers';
import { RatingSection } from './mapper-types';
import { BadgeStatusType } from '@/components/Badge';

interface CockpitSection {
  assessmentItems: AssessmentItem[];
  summaryTitle: string;
  summaryContent: string | null;
}

interface InteriorSection {
  visualRating: 1 | 2 | 3 | 4 | null;
  assessmentItems: AssessmentItem[];
  summaryTitle: string;
  summaryContent: string | null;
}

export interface MappedCarInteriorSection {
  ratingSection: RatingSection;
  remarks: string[];
  cockpitSection: CockpitSection;
  interiorSection: InteriorSection;
  equipmentItems: InspectionItem[];
  equipmentExpertSummary: string | null;
}

export const mapCarInteriorSectionToUI = (
  section: PublicAccessCarInteriorSectionDto | null | undefined,
): MappedCarInteriorSection | null => {
  if (!section) {
    return null;
  }

  const summary = section.reportSectionSummary;
  const rating = mapScoreToNumber(summary?.score);

  const dashboard = section.carDashboardComponentsSubSection;
  const interior = section.carInteriorComponentsSubSection;
  const equipment = section.carEquipmentSubSection;

  // Map cockpit assessment items
  const cockpitItems: AssessmentItem[] = [
    {
      id: '1',
      title: 'Plastiki',
      iconUrl: 'pp',
      badgeText: dashboard?.plasticParts,
      description: dashboard?.plasticPartsRemark ?? '',
      status: mapAssessmentStatus(dashboard?.plasticParts),
    },
    {
      id: '2',
      title: 'Drewno',
      iconUrl: 'justice-scale',
      badgeText: dashboard?.woodParts,
      description: dashboard?.woodPartsRemark,
      status: mapAssessmentStatus(dashboard?.woodParts),
    },
    {
      id: '3',
      title: 'Elementy metalowe',
      iconUrl: 'magnifying-glass',
      badgeText: dashboard?.metalParts,
      description: dashboard?.metalPartsRemark,
      status: mapAssessmentStatus(dashboard?.metalParts),
    },
  ];

  // Map interior assessment items
  const interiorItems: AssessmentItem[] = [
    {
      id: '4',
      title: 'Zabrudzenia',
      iconUrl: 'brud',
      description: interior?.stainingRemark,
      badgeText: interior?.staining,
      status: mapAssessmentStatusInner(interior?.staining),
    },
    {
      id: '5',
      title: 'Przetarcia/zarysowania',
      iconUrl: "flame-fire",
      badgeText: interior?.abrasion,
      description: interior?.abrasionRemark,
      status: mapAssessmentStatusInner(interior?.abrasion),
    },
    {
      id: '6',
      title: 'Pęknięcia',
      iconUrl: 'broken',
      badgeText: interior?.cracking,
      description: interior?.crackingRemark,
      status: mapAssessmentStatusInner(interior?.cracking),
    },
    {
      id: '7',
      title: 'Ślady palenia',
      iconUrl: 'fire',
      badgeText: interior?.burnMarks,
      description: interior?.burnMarksRemark,
      status: mapAssessmentStatusInner(interior?.burnMarks),
    },
    {
      id: '8',
      title: 'Popielniczka',
      iconUrl: 'smooke',
      badgeText: interior?.ashTray,
      description: interior?.ashTrayRemark,
      status: mapAssessmentStatusAsh(interior?.ashTray),
    },
    {
      id: '9',
      title: 'Zapalniczka',
      badgeText: interior?.lighter,
      description: interior?.lighterRemark,
      iconUrl: 'monitor-display',
      status: mapAssessmentStatusAsh(interior?.lighter),
    },
  ];

  // Map equipment items
  const equipmentItems: InspectionItem[] = [
    {
      id: '1',
      name: 'Centralny zamek',
      status: mapEquipmentStatusToBadge(equipment?.centralLock),
      comments: equipment?.centralLockRemark,
    },
    {
      id: '2',
      name: 'Radio',
      status: mapEquipmentStatusToBadge(equipment?.radioReceiver),
      comments: equipment?.radioReceiverRemark,
    },
    {
      id: '3',
      name: 'Nawigacja/CD',
      status: mapEquipmentStatusToBadge(equipment?.navigationCdPlayer),
      comments: equipment?.navigationCdPlayerRemark,
    },
    {
      id: '4',
      name: 'Klimatyzacja',
      status: mapEquipmentStatusToBadge(equipment?.airConditioning),
      comments: equipment?.airConditioningRemark,
    },
    {
      id: '5',
      name: 'Podgrz. tylnej szyby',
      status: mapEquipmentStatusToBadge(equipment?.rearWindowHeating),
      comments: equipment?.rearWindowHeatingRemark,
    },
    {
      id: '6',
      name: 'El. reg. lusterek',
      status: mapEquipmentStatusToBadge(equipment?.electricMirrorAdjustment),
      comments: equipment?.electricMirrorAdjustmentRemark,
    },
    {
      id: '7',
      name: 'El. składanie lusterek',
      status: mapEquipmentStatusToBadge(equipment?.electricMirrorFolding),
      comments: equipment?.electricMirrorFoldingRemark,
    },
    {
      id: '8',
      name: 'Wycieraczki',
      status: mapEquipmentStatusToBadge(equipment?.windshieldWipers),
      comments: equipment?.windshieldWipersRemark,
    },
    {
      id: '9',
      name: 'Spryskiwacz szyby',
      status: mapEquipmentStatusToBadge(equipment?.windshieldWashers),
      comments: equipment?.windshieldWashersRemark,
    },
    {
      id: '10',
      name: 'Sygnał dźwiękowy',
      status: mapEquipmentStatusToBadge(equipment?.horn),
      comments: equipment?.hornRemark,
    },
    {
      id: '11',
      name: 'El. szyby przód',
      status: mapEquipmentStatusToBadge(equipment?.electricFrontWindows),
      comments: equipment?.electricFrontWindowsRemark,
    },
    {
      id: '12',
      name: 'El. szyby tył',
      status: mapEquipmentStatusToBadge(equipment?.electricRearWindows),
      comments: equipment?.electricRearWindowsRemark,
    },
    {
      id: '13',
      name: 'Kierownica wielofunkcyjna',
      status: mapEquipmentStatusToBadge(equipment?.multifunctionSteeringWheel),
      comments: equipment?.multifunctionSteeringWheelRemark,
    },
    {
      id: '14',
      name: 'Komputer',
      status: mapEquipmentStatusToBadge(equipment?.onboardComputer),
      comments: equipment?.onboardComputerRemark,
    },
    {
      id: '15',
      name: 'Wspomaganie kierownicy',
      status: mapEquipmentStatusToBadge(equipment?.powerSteering),
      comments: equipment?.powerSteeringRemark,
    },
    {
      id: '16',
      name: 'Podgrzewanie siedzeń',
      status: mapEquipmentStatusToBadge(equipment?.seatHeating),
      comments: equipment?.seatHeatingRemark,
    },
    {
      id: '17',
      name: 'Elektryczna regulacja siedzenia',
      status: mapEquipmentStatusToBadge(equipment?.electricSeatAdjustment),
      comments: equipment?.electricSeatAdjustmentRemark,
    },
    {
      id: '18',
      name: 'Otwieranie bagażnika',
      status: mapEquipmentStatusToBadge(equipment?.trunkOpening),
      comments: equipment?.trunkOpeningRemark,
    },
    {
      id: '19',
      name: 'Otwieranie maski',
      status: mapEquipmentStatusToBadge(equipment?.hoodOpening),
      comments: equipment?.hoodOpeningRemark,
    },
    {
      id: '20',
      name: 'Otwieranie wlewu paliwa',
      status: mapEquipmentStatusToBadge(equipment?.fuelFillerLeverOpening),
      comments: equipment?.fuelFillerLeverOpeningRemark,
    },
    {
      id: '21',
      name: 'Szyberdach',
      status: mapEquipmentStatusToBadge(equipment?.sunroof),
      comments: equipment?.sunroofRemark,
    },
    {
      id: '22',
      name: 'Skórzana tapicerka',
      status: mapEquipmentStatusToBadge(equipment?.leatherUpholstery),
      comments: equipment?.leatherUpholsteryRemark,
    },
    {
      id: '23',
      name: 'Poduszki powietrzne (ogłoszony wizualnie i kontrolki)',
      status: mapEquipmentStatusToBadge(equipment?.airbags),
      comments: equipment?.airbagsRemark,
    },
    {
      id: '24',
      name: 'Ilość kluczyków',
      status: BadgeStatusType.Info,
      customBadge: equipment?.numberOfKeys !== null ? <div className="flex justify-center items-center flex-grow-0 flex-shrink-0 h-7 relative gap-2.5 px-[9px] py-2 rounded-[50px] bg-[#112562]">
        <div className="flex-grow-0 flex-shrink-0 text-[15px] font-bold text-left uppercase text-[#b3d1ff]">
          {equipment?.numberOfKeys}
        </div>
      </div> : <></>,
      comments: equipment?.numberOfKeysRemark,
    },
    {
      id: '25',
      name: 'Otwierany dach (kabriolet)',
      status: mapEquipmentStatusToBadge(equipment?.convertibleRoof),
      comments: equipment?.convertibleRoofRemark,
    },
    {
      id: '26',
      name: 'Czujniki parkowania tył',
      status: mapEquipmentStatusToBadge(equipment?.rearParkingSensors),
      comments: equipment?.rearParkingSensorsRemark,
    },
    {
      id: '27',
      name: 'Czujniki parkowania przód',
      status: mapEquipmentStatusToBadge(equipment?.frontParkingSensors),
      comments: equipment?.frontParkingSensorsRemark,
    },
    {
      id: '28',
      name: 'Kamera cofania',
      status: mapEquipmentStatusToBadge(equipment?.rearViewCamera),
      comments: equipment?.rearViewCameraRemark,
    },
    {
      id: '29',
      name: 'Oświetlenie wnętrza',
      status: mapEquipmentStatusToBadge(equipment?.interiorLighting),
      comments: equipment?.interiorLightingRemark,
    },
    {
      id: '30',
      name: 'Sterowanie nawiewem',
      status: mapEquipmentStatusToBadge(equipment?.airVentControl),
      comments: equipment?.airVentControlRemark,
    },
    {
      id: '33',
      name: 'Osłony przeciwsłoneczne',
      status: mapEquipmentStatusToBadge(equipment?.sunVisors),
      comments: equipment?.sunVisorsRemark,
    },
  ];

  return {
    ratingSection: {
      title: 'Ocena kontroli wnętrza',
      rating,
      ratingMessage: rating !== null ? getRatingMessage(rating) : 'Brak oceny',
      description: summary?.remarks && summary?.remarks?.length > 0 ? "Zwróć uwagę na podsumowanie wybranych zastrzeżeń dotyczących tej sekcji." : null,
      defectFree: summary?.defectFree ?? false,
    },
    remarks: summary?.remarks || [],
    cockpitSection: {
      assessmentItems: cockpitItems,
      summaryTitle: 'Podsumowanie Rzeczoznawcy dotyczące  stanu elementów kokpitu',
      summaryContent: dashboard?.expertSummary || null,
    },
    interiorSection: {
      visualRating: mapVisualAssessmentToRating(interior?.visualWearAssessment),
      assessmentItems: interiorItems,
      summaryTitle: 'Podsumowanie Rzeczoznawcy dotyczące stanu elementów wnętrza',
      summaryContent: interior?.expertSummary || null,
    },
    equipmentItems,
    equipmentExpertSummary: equipment?.expertSummary || null,
  };
};
